import { apiClient } from './apiClient';

export type BuyerPersonaSeniority =
  | 'IndividualContributor'
  | 'Manager'
  | 'Director'
  | 'VP'
  | 'CLevel'
  | 'Other';

export type BuyerPersona = {
  id: string;
  name: string;
  description: string;
  jobTitle: string;
  seniority: BuyerPersonaSeniority;
  painPoints: string;
  goals: string;
  demographicsJson?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type BuyerPersonaListResult = {
  items: BuyerPersona[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type BuyerPersonaUpsertPayload = {
  name: string;
  description?: string;
  jobTitle?: string;
  seniority?: BuyerPersonaSeniority;
  painPoints?: string;
  goals?: string;
  demographicsJson?: string;
  isActive?: boolean;
  sortOrder?: number;
};

export type SalesProductPersonaLink = {
  buyerPersonaId: string;
  name: string;
  jobTitle: string;
  seniority: BuyerPersonaSeniority;
  isPrimary: boolean;
  fitScore: number;
  sortOrder: number;
};

export type SalesProductPersonaLinkItemPayload = {
  buyerPersonaId: string;
  isPrimary?: boolean;
  fitScore?: number;
  sortOrder: number;
};

export type BuyerPersonaListFilter = {
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
};

export async function listBuyerPersonas(
  filter: BuyerPersonaListFilter = {},
): Promise<BuyerPersonaListResult> {
  const response = await apiClient.get<BuyerPersonaListResult>('/v1/sales/buyer-personas', {
    params: {
      activeOnly: filter.activeOnly ?? false,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? 25,
      search: filter.search || undefined,
    },
  });
  return response.data;
}

export async function createBuyerPersona(payload: BuyerPersonaUpsertPayload): Promise<BuyerPersona> {
  const response = await apiClient.post<BuyerPersona>('/v1/sales/buyer-personas', payload);
  return response.data;
}

export async function updateBuyerPersona(
  id: string,
  payload: BuyerPersonaUpsertPayload,
): Promise<BuyerPersona> {
  const response = await apiClient.put<BuyerPersona>(`/v1/sales/buyer-personas/${id}`, payload);
  return response.data;
}

export async function deleteBuyerPersona(id: string): Promise<void> {
  await apiClient.delete(`/v1/sales/buyer-personas/${id}`);
}

export type BuyerPersonaImportQueued = {
  importId: string;
  hangfireJobId?: string | null;
  status: string;
  message: string;
};

export type BuyerPersonaImportStatus = {
  id: string;
  originalFileName: string;
  status: string;
  hangfireJobId?: string | null;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  errorSummary?: string | null;
  createdAtUtc: string;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
};

export async function downloadBuyerPersonaImportTemplate(): Promise<Blob> {
  const response = await apiClient.get('/v1/sales/buyer-personas/import/template', {
    responseType: 'blob',
  });
  return response.data;
}

export async function importBuyerPersonas(file: File): Promise<BuyerPersonaImportQueued> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<BuyerPersonaImportQueued>(
    '/v1/sales/buyer-personas/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function getBuyerPersonaImportStatus(
  importId: string,
): Promise<BuyerPersonaImportStatus> {
  const { data } = await apiClient.get<BuyerPersonaImportStatus>(
    `/v1/sales/buyer-personas/import/${importId}`,
  );
  return data;
}

export async function getProductBuyerPersonas(productId: string): Promise<SalesProductPersonaLink[]> {
  const response = await apiClient.get<SalesProductPersonaLink[]>(
    `/v1/sales/products/${productId}/buyer-personas`,
  );
  return response.data;
}

export async function replaceProductBuyerPersonas(
  productId: string,
  items: SalesProductPersonaLinkItemPayload[],
): Promise<void> {
  await apiClient.put(`/v1/sales/products/${productId}/buyer-personas`, { items });
}
